import {LLMRole} from 'graphql-client/__generated/graphqlGlobalTypes';

import {CitationGenerationMode} from './ChatDAO';
import {
  StateUpdateChatEventNode,
  RequestStateUpdateChatEventNode,
  DisplayMessageChatEventNode,
  ClientChatEvent,
} from './messages/MessageTypes';

interface ChatSystemState {
  error_message?: string;
}

function getLatestStateUpdateEvent(events: Array<ClientChatEvent>): StateUpdateChatEventNode | undefined {
  return events.findLast(
    (event): event is StateUpdateChatEventNode => event.__typename === 'StateUpdateChatEventNode'
  );
}

function getLatestRequestStateUpdateEvent(
  events: Array<ClientChatEvent>
): RequestStateUpdateChatEventNode | undefined {
  return events.findLast(
    (event): event is RequestStateUpdateChatEventNode =>
      event.__typename === 'RequestStateUpdateChatEventNode'
  );
}

function getLatestUserDisplayMessageEvent(
  events: Array<ClientChatEvent>
): DisplayMessageChatEventNode | undefined {
  return events.findLast(
    (event): event is DisplayMessageChatEventNode =>
      event.__typename === 'DisplayMessageChatEventNode' && event.role === LLMRole.USER
  );
}

export function getLatestApplicationState(events: Array<ClientChatEvent>): object | null {
  const stateUpdateEvent = getLatestStateUpdateEvent(events);
  return stateUpdateEvent?.newState.chat_state.application_state ?? null;
}

export function getLatestSystemState(events: Array<ClientChatEvent>): ChatSystemState | null {
  const stateUpdateEvent = getLatestStateUpdateEvent(events);
  return stateUpdateEvent?.newState.chat_state.system_state ?? null;
}

/**
 * Get the publicDataEnabled setting from the last RequestStateUpdateChatEventNode.
 * Falls back to false if not found.
 */
export function getLatestPublicDataEnabled(events: Array<ClientChatEvent>): boolean {
  const requestStateUpdateEvent = getLatestRequestStateUpdateEvent(events);
  return requestStateUpdateEvent?.publicDataEnabled ?? false;
}

/**
 * Get the citationGenerationMode setting from the last user DisplayMessageChatEventNode.
 * The mode is persisted per-message on the user's display event. Falls back to AUTO if not found.
 */
export function getLatestCitationGenerationMode(events: Array<ClientChatEvent>): CitationGenerationMode {
  const userDisplayEvent = getLatestUserDisplayMessageEvent(events);
  const citationGenerationMode = userDisplayEvent?.citationGenerationMode;
  if (
    citationGenerationMode != null &&
    Object.values(CitationGenerationMode).includes(citationGenerationMode)
  ) {
    return citationGenerationMode;
  }
  return CitationGenerationMode.AUTO;
}
